/**
 * Internationalization (i18n) Infrastructure
 *
 * Lightweight React Context solution for multi-language support.
 * Supports English and German with localStorage persistence.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import en from './translations/en.js';
import de from './translations/de.js';

const translations = { en, de };
const STORAGE_KEY = 'waterloo-language';
const DEFAULT_LANGUAGE = 'en';

/**
 * Detect preferred language from localStorage or browser settings.
 * @returns {'en' | 'de'} Detected language code
 */
function detectLanguage() {
  // Check localStorage first
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && translations[stored]) return stored;
  }

  // Fall back to browser language
  if (typeof navigator !== 'undefined') {
    const browserLang = navigator.language?.slice(0, 2);
    if (browserLang === 'de') return 'de';
  }

  return DEFAULT_LANGUAGE;
}

const I18nContext = createContext(null);

/**
 * Interpolate placeholders in translation string.
 * E.g., "Hello {name}" with { name: "Alice" } => "Hello Alice"
 */
function interpolate(template, values = {}) {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return values[key] !== undefined ? String(values[key]) : match;
  });
}

/**
 * I18n Provider component.
 * Wrap your app with this to enable translations.
 */
export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(detectLanguage);

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, lang);
      }
    }
  }, []);

  const t = useCallback((key, values) => {
    const translation =
      translations[language]?.[key] ||
      translations[DEFAULT_LANGUAGE]?.[key] ||
      key;
    return values ? interpolate(translation, values) : translation;
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      availableLanguages: Object.keys(translations),
    }),
    [language, setLanguage, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Hook to access translation function and language state.
 * @returns {{ t: Function, language: string, setLanguage: Function, availableLanguages: string[] }}
 */
export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }
  return context;
}

export { I18nContext };
